'use client';

import { useState, useRef, useEffect } from 'react';
import { Selection } from 'react-aria-components';

import ChevronIcon from '@/fo/icons/base/icons/chevron.svg';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { UiKitListboxBase } from '@/fo/ui/kit/listbox/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';

import { FilterOption } from '../../types';

import { styles } from './SortBlock.styles';

export interface SortBlockProps {
  sortByFilters: FilterOption[];
  selectedSortByFilters: string;
  onSortChange: (option: FilterOption | null) => void;
  className?: string;
  isLoading?: boolean;
}
export function SortBlock({
  sortByFilters = [],
  selectedSortByFilters = '',
  onSortChange,
  className,
  isLoading
}: SortBlockProps) {
  const slots = styles();
  const isDesktop = useMediaQuery('sm');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const selected =
    sortByFilters.find((s) => s.value === selectedSortByFilters) ||
    sortByFilters[0];

  const handleSortChange = (value: Selection) => {
    if (value === 'all') {
      return;
    }

    const selectedOption = sortByFilters.find(
      (option) => option.value === Array.from(value)[0]
    );

    onSortChange?.(selectedOption as FilterOption);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  if (isLoading) {
    return <span className={slots.loading()}></span>;
  }

  if (isDesktop) {
    return (
      <div
        className={slots.base({ className })}
        ref={popoverRef}
      >
        <button
          ref={triggerRef}
          className={slots.sortButton()}
          onClick={() => setOpen(!open)}
        >
          <ChevronIcon className={slots.icon({ open })} />
          {
            sortByFilters.find(
              (option) => option.value === selectedSortByFilters
            )?.label
          }
        </button>
        <UiKitListboxBase
          options={sortByFilters}
          triggerRef={triggerRef}
          isOpen={open}
          onSelectionChange={handleSortChange}
          onOpenChange={setOpen}
          isNonModal={true}
          selectedKeys={new Set([selected.value])}
          value={
            sortByFilters.find(
              (option) => option.value === selectedSortByFilters
            )?.value
          }
        />
      </div>
    );
  }

  return (
    <UiKitDrawerBase
      title={selected.label}
      open={open}
      trigger={
        <button className={slots.sortButton()}>
          <ChevronIcon className={slots.sortArrow()} />

          <span className={slots.sortButtonLbl()}>
            {
              sortByFilters.find(
                (option) => option.value === selectedSortByFilters
              )?.label
            }
          </span>
        </button>
      }
      onOpenChange={setOpen}
      className="rounded-tl-[20px] rounded-tr-[20px]"
    >
      <ul className={slots.sortValueList()}>
        {sortByFilters.map((option) => (
          <li
            className={slots.sortValue()}
            key={option.value}
            onClick={() => {
              onSortChange(option);
              setOpen(false);
            }}
          >
            {option.label}
          </li>
        ))}
      </ul>
    </UiKitDrawerBase>
  );
}
